import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { goTo } from "../lib/nav";
import { trackEvent } from "../lib/seo";
import { livePing } from "../lib/live";
import { ShoppingBag, ArrowRight, Check } from "lucide-react";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

const load = (k, d) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; }
};

export function AppProvider({ children }) {
  const [cart, setCart] = useState(() => load("cs_cart", []));
  const [favorites, setFavorites] = useState(() => load("cs_favorites", []));
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const openCart = useCallback(() => setDrawerOpen(true), []);
  const closeCart = useCallback(() => setDrawerOpen(false), []);

  useEffect(() => { localStorage.setItem("cs_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("cs_favorites", JSON.stringify(favorites)); }, [favorites]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    const token = localStorage.getItem("cs_token");
    if (token) api.get("/auth/me").then((r) => setUser(r.data)).catch(() => localStorage.removeItem("cs_token"));
  }, []);

  const refreshUser = useCallback(() => {
    const token = localStorage.getItem("cs_token");
    if (token) api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
  }, []);

  const addToCart = useCallback((item, opts = {}) => {
    trackEvent("add_to_cart", item.name);
    livePing("add", item.name);
    setCart((prev) => {
      if (item.kind === "product" || item.kind === "cupcookie" || item.kind === "limited") {
        const idx = prev.findIndex((p) => p.product_id === item.product_id && p.kind === item.kind && !p.composition);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], quantity: next[idx].quantity + item.quantity };
          return next;
        }
      }
      return [...prev, { ...item, line_id: Math.random().toString(36).slice(2) }];
    });
    if (opts.silent) { toast.success(`${item.name} ajouté au panier`); return; }
    toast.custom((id) => (
      <div data-testid="cart-added-toast" className="w-[340px] max-w-[90vw] rounded-2xl bg-white shadow-xl ring-1 ring-[#E7DACB] overflow-hidden">
        <div className="flex items-center gap-3 p-3">
          {item.image ? (
            <img loading="lazy" decoding="async" src={item.image?.startsWith("/api/files/") ? `${item.image}?w=220` : item.image} alt="" className="h-14 w-14 rounded-xl object-cover flex-shrink-0" />
          ) : (
            <div className="h-14 w-14 rounded-xl bg-[#F3E9DC] grid place-items-center flex-shrink-0">
              <ShoppingBag size={22} className="text-[#C6531A]" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[#1E7F4F] text-xs font-semibold">
              <Check size={14} /> Ajouté au panier
            </div>
            <p className="font-serif text-[#2B170C] text-sm font-semibold truncate mt-0.5">{item.name}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 px-3 pb-3">
          <button
            data-testid="cart-toast-continue-btn"
            onClick={() => toast.dismiss(id)}
            className="rounded-full border border-[#E7DACB] text-[#6E5544] text-xs font-semibold py-2.5 hover:bg-[#FAF6F0] transition-colors">
            Continuer mes achats
          </button>
          <button
            data-testid="cart-toast-view-cart-btn"
            onClick={() => { toast.dismiss(id); setDrawerOpen(true); }}
            className="rounded-full bg-[#C6531A] text-white text-xs font-semibold py-2.5 inline-flex items-center justify-center gap-1.5 hover:bg-[#AE4514] transition-colors">
            Voir le panier <ArrowRight size={14} />
          </button>
        </div>
      </div>
    ), { duration: 5000 });
  }, []);

  const updateQty = useCallback((line_id, qty) => {
    setCart((prev) => prev.map((p) => (p.line_id === line_id ? { ...p, quantity: Math.max(1, qty) } : p)));
  }, []);

  const removeFromCart = useCallback((line_id) => {
    setCart((prev) => prev.filter((p) => p.line_id !== line_id));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);
  const convertToBox = useCallback((lineIds, box) => {
    setCart((prev) => [...prev.filter((p) => !lineIds.includes(p.line_id)), { ...box, line_id: Math.random().toString(36).slice(2) }]);
  }, []);

  const toggleFavorite = useCallback((product) => {
    setFavorites((prev) => {
      const exists = prev.find((f) => f.id === product.id);
      if (exists) { toast("Retiré des favoris"); return prev.filter((f) => f.id !== product.id); }
      toast.success("Ajouté aux favoris ♡");
      return [...prev, product];
    });
  }, []);

  const isFavorite = useCallback((id) => favorites.some((f) => f.id === id), [favorites]);

  const login = (data) => { localStorage.setItem("cs_token", data.token); setUser(data); };
  const logout = () => { localStorage.removeItem("cs_token"); setUser(null); };

  // Panier abandonné : copie du panier côté serveur pour les clientes connectées ou ayant saisi leur e-mail.
  const [cartContact, setCartContact] = useState(null);
  const snapRef = useRef("");
  const emailRef = useRef("");

  // Lien « Je finalise ma commande » d'un e-mail de relance : remet le panier, même sur un autre appareil.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get("retrouver");
    if (!tok) return;
    params.delete("retrouver");
    window.history.replaceState(null, "", window.location.pathname + (params.toString() ? `?${params}` : "") + window.location.hash);
    api.get(`/cart/restore/${encodeURIComponent(tok)}`).then(({ data }) => {
      if (data.already_ordered) { toast("Cette commande a déjà été passée, merci 🤎"); return; }
      if (!data.items.length) { toast("Les produits de ce panier ne sont plus disponibles."); return; }
      const sig = (i) => JSON.stringify([i.kind, i.product_id, i.name, i.composition || null]);
      setCart((prev) => {
        const have = new Set(prev.map(sig));
        return [...prev, ...data.items.filter((i) => !have.has(sig(i))).map((i) => ({ ...i, line_id: Math.random().toString(36).slice(2) }))];
      });
      if (data.email) setCartContact((c) => c || { email: data.email, name: data.name || "" });
      toast.success(data.dropped ? "Ton panier est de retour (certains produits ne sont plus disponibles)" : "Ton panier est de retour 🤎");
    }).catch(() => toast.error("Ce lien de panier n'est plus valable."));
  }, []);
  useEffect(() => {
    const email = user?.email || cartContact?.email || "";
    if (user?.role === "admin" || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return undefined;
    const items = cart.map((i) => ({
      name: i.name, quantity: i.quantity, unit_price: i.unit_price, image: i.image || "", kind: i.kind || "",
      details: [(i.composition || []).map((c) => `${c.name}${c.quantity ? ` × ${c.quantity}` : ""}`).join(", "), i.gift ? `Pour ${i.gift.recipient_name}` : ""].filter(Boolean).join(" · "),
    }));
    const body = { items, lines: cart.map(({ line_id, ...rest }) => rest), email, prev_email: emailRef.current && emailRef.current !== email ? emailRef.current : "", name: cartContact?.name || "", phone: cartContact?.phone || "" };
    const sig = JSON.stringify(body);
    if (sig === snapRef.current || (!items.length && !localStorage.getItem("cs_cart_snap"))) return undefined;
    const t = setTimeout(() => {
      snapRef.current = sig;
      emailRef.current = email;
      api.post("/cart/snapshot", body).then(() => {
        if (items.length) localStorage.setItem("cs_cart_snap", "1"); else localStorage.removeItem("cs_cart_snap");
      }).catch(() => {});
    }, 2000);
    return () => clearTimeout(t);
  }, [cart, user, cartContact]);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);

  return (
    <AppCtx.Provider value={{
      cart, addToCart, updateQty, removeFromCart, clearCart, convertToBox, cartCount, cartSubtotal,
      favorites, toggleFavorite, isFavorite,
      user, login, logout, refreshUser, settings,
      drawerOpen, openCart, closeCart, setCartContact,
    }}>
      {children}
    </AppCtx.Provider>
  );
}
